import type { AdminSection } from './AdminSidebar';
import type { TemplateConfig } from '@/mocks/template-config';
import type { CompanyData } from '@/hooks/useCompanyData';
import type { TemplatePreset } from '@/mocks/template-presets';
import CompanyForm from './CompanyForm';
import TopForm from './TopForm';
import MainImageForm from './MainImageForm';
import ClientBoxForm from './ClientBoxForm';
import InstitutionalForm from './InstitutionalForm';
import FooterForm from './FooterForm';
import SpecialtiesForm from './SpecialtiesForm';
import PDFForm from './PDFForm';
import PresetLibraryForm from './PresetLibraryForm';

interface AdminFormsProps {
  activeSection: AdminSection;
  templateConfig: TemplateConfig;
  companyData: CompanyData;
  presets: TemplatePreset[];
  activePresetId: string | null;
  onUpdateTemplate: (config: TemplateConfig) => void;
  onUpdateCompany: (data: CompanyData) => void;
  onSetActivePreset: (id: string | null) => void;
  onTogglePreset: (id: string) => void;
  onSavePreset: (preset: TemplatePreset) => void;
}

export default function AdminForms({
  activeSection,
  templateConfig,
  companyData,
  presets,
  activePresetId,
  onUpdateTemplate,
  onUpdateCompany,
  onSetActivePreset,
  onTogglePreset,
  onSavePreset,
}: AdminFormsProps) {
  const updateSection = <K extends keyof TemplateConfig>(key: K, value: TemplateConfig[K]) => {
    onUpdateTemplate({ ...templateConfig, [key]: value });
  };

  switch (activeSection) {
    case 'company':
      return <CompanyForm data={companyData} onChange={onUpdateCompany} />;
    case 'top':
      return <TopForm config={templateConfig.top} onChange={(v) => updateSection('top', v)} />;
    case 'mainImage':
      return <MainImageForm config={templateConfig.mainImage} onChange={(v) => updateSection('mainImage', v)} />;
    case 'clientBox':
      return <ClientBoxForm config={templateConfig.clientBox} onChange={(v) => updateSection('clientBox', v)} />;
    case 'institutional':
      return <InstitutionalForm config={templateConfig.institutional} onChange={(v) => updateSection('institutional', v)} />;
    case 'footer':
      return <FooterForm config={templateConfig.footer} onChange={(v) => updateSection('footer', v)} />;
    case 'specialties':
      return <SpecialtiesForm specialties={templateConfig.specialties} onChange={(v) => updateSection('specialties', v)} />;
    case 'pdf':
      return (
        <PDFForm
          cover={templateConfig.cover}
          backCover={templateConfig.backCover}
          pdf={templateConfig.pdf}
          onChangeCover={(v) => updateSection('cover', v)}
          onChangeBackCover={(v) => updateSection('backCover', v)}
          onChangePdf={(v) => updateSection('pdf', v)}
        />
      );
    case 'presets':
      return (
        <PresetLibraryForm
          presets={presets}
          activePresetId={activePresetId}
          templateConfig={templateConfig}
          onSetActive={onSetActivePreset}
          onTogglePreset={onTogglePreset}
          onSavePreset={onSavePreset}
          onApplyPreset={(preset) => {
            const next: TemplateConfig = {
              ...templateConfig,
              top: {
                ...templateConfig.top,
                bannerImage: preset.topBannerDesktop || templateConfig.top.bannerImage,
                title: preset.title || templateConfig.top.title,
                subtitle: preset.subtitle || templateConfig.top.subtitle,
              },
              mainImage: {
                ...templateConfig.mainImage,
                image: preset.mainImage || templateConfig.mainImage.image,
              },
              cover: {
                ...templateConfig.cover,
                image: preset.pdfCoverImage || templateConfig.cover.image,
                title: preset.title || templateConfig.cover.title,
                subtitle: preset.subtitle || templateConfig.cover.subtitle,
              },
              backCover: {
                ...templateConfig.backCover,
                image: preset.pdfBackCoverImage || templateConfig.backCover.image,
              },
              footer: {
                ...templateConfig.footer,
                bannerImage: preset.footerImage || templateConfig.footer.bannerImage,
              },
              specialties: preset.activeSpecialties.length > 0
                ? preset.activeSpecialties
                : templateConfig.specialties,
              activePresetId: preset.id,
            };
            onUpdateTemplate(next);
          }}
        />
      );
    default:
      return null;
  }
}